import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Grid, Paper, Typography, useMediaQuery, Card, CardContent, Checkbox, CircularProgress, Box, Divider } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import axios from "axios";
import Dropdown from "../components/Dropdown"; // Reusable Dropdown component
import { Stack } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAuth } from "../context/AuthContext";
import CustomNoDataOverlay from "../components/CustomNoDataOverlay";

const ReAssignComplaintDetails = ({ loggedInUser, setLoggedInUser, connectionString }) => {
  // State
  const [usersOptions, setUsersOptions] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState([]); // array of complainId (numbers)
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ assign: "", assignName: "", reAssign: "", reAssignName: "" });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const { user, selectedCompany } = useAuth();

  const companyId = selectedCompany?.CompId || null;

  const path = connectionString;
  const isDesktop = useMediaQuery('(min-width:1124px)');

  // Fetch users options when companyId is available
  useEffect(() => {
    // const companyId = loggedInUser?.companyId || loggedInUser?.CompID || loggedInUser?.CompId;
    if (!companyId) return;
    axios.get(`${path}/api/users`, { params: { companyId } }).then((res) => { setUsersOptions(Array.isArray(res.data.data) ? res.data.data : []) }).catch((error) => console.error("Error fetching users:", error));
  }, [companyId]);

  // Dropdown handlers
  const handleAssignChange = (selected) => { setFilters((prev) => ({ ...prev, assign: selected.UserId || selected.UserId || "", assignName: selected.employeeName || selected.employeeName })); };
  const handleReAssignChange = (selected) => { setFilters((prev) => ({ ...prev, reAssign: selected.UserId || selected.UserId || "", reAssignName: selected.employeeName || selected.employeeName })); };

  // Fetch inquiries
  const fetchInquiries = (assignName = "") => {
    setLoading(true);
    axios.get(`${path}/api/reassign-complain`, {
      params: { assign: filters.assignName },
      headers: {
        'Cache-Control': 'no-store' // helpful for intermediate caches; browsers may ignore it for XHR
      }
    })
      .then((res) => {
        // expect res.data.data to be array
        console.log(res.data.data)
        setInquiries(res.data.data || []);
        setSelectedComplaint([]); // reset selection
      })
      .catch((error) => {
        console.error("Error fetching inquiries:", error);
        setInquiries([]);
      })
      .finally(() => setLoading(false));
  };


  useEffect(() => {
    fetchInquiries();
  }, []);

  // Reset
  // const handleResetFilters = () => {
  //   setFilters({ assign: "", assignName: "", reAssign: "", reAssignName: "" });
  //   setInquiries([]);
  //   setSelectedComplaint([]);
  // };


  const handleResetFilters = () => {
    // clear dropdown values
    setFilters({
      assign: "",
      assignName: "",
      reAssign: "",
      reAssignName: ""
    });

    // clear selection
    setSelectedComplaint([]);

    // reload all complaints
    fetchInquiries("");
  };

  // Checkbox handlers (mobile)
  const handleCheckboxChange = (complainId) => { setSelectedComplaint((prevSelected) => prevSelected.includes(complainId) ? prevSelected.filter((id) => id !== complainId) : [...prevSelected, complainId]); };

  // Header Select-All handler
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = inquiries.map((c) => c.complainId);
      setSelectedComplaint(allIds);
    } else {
      setSelectedComplaint([]);
    }
  };

  // Submit Handler
  const handleSubmit = () => {
    if (!filters.assign || !filters.reAssign) {
      setSnackbar({ open: true, message: "Please select values for Assign and Re-Assign.", severity: "warning" });
      return;
    }
    if (selectedComplaint.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one complaint.", severity: "warning" });
      return;
    }
    if (filters.assign === filters.reAssign && filters.assignName === filters.reAssignName) {
      setSnackbar({ open: true, message: "Assign and Re-Assign cannot be the same.", severity: "warning" });
      return;
    }
    const submissionData = {
      assignId: filters.assign,
      assign: filters.assignName,
      reAssignId: filters.reAssign,
      reAssign: filters.reAssignName,
      complainIds: selectedComplaint,
    };
    console.log("Submitting Data:", submissionData);
    axios.post(`${path}/api/reassign-complain`, submissionData)
      .then((res) => {
        setSnackbar({ open: true, message: "Submission successful!", severity: "success" });
        handleResetFilters();
      })
      .catch((error) => {
        console.error("Error submitting inquiries:", error);
        setSnackbar({ open: true, message: "Error during submission. Please try again.", severity: "error" });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    // en-GB dd-mm-yyyy
    return d.toLocaleDateString("en-GB").replace(/\//g, "-");
  };

  // normalize possible values to a displayable string
  const toStringValue = (v) => {
    if (v === undefined || v === null) return "";
    if (typeof v === "string" || typeof v === "number") return String(v);
    if (Array.isArray(v)) return v.map(toStringValue).filter(Boolean).join(", ");
    if (typeof v === "object") {
      // common object shape fallbacks
      if (v.city) return String(v.city);
      if (v.name) return String(v.name);
      if (v.state) return String(v.state);
      if (v.label) return String(v.label);
      // last resort: stringify short
      try {
        const s = JSON.stringify(v);
        return s.length > 80 ? s.slice(0, 77) + "..." : s;
      } catch {
        return String(v);
      }
    }
    return String(v);
  };

  // DataGrid columns for desktop — first column is custom checkbox column
  const columns = [
    {
      field: 'select', headerName: '', flex: 0.3, minWidth: 70, sortable: false, filterable: false,
      renderHeader: () => {
        const allChecked = selectedComplaint.length === inquiries.length && inquiries.length > 0;
        const someChecked = selectedComplaint.length > 0 && selectedComplaint.length < inquiries.length;
        return (<Checkbox checked={allChecked} indeterminate={someChecked} onChange={(e) => handleSelectAll(e.target.checked)} inputProps={{ 'aria-label': 'select all complaints' }} />);
      },
      renderCell: (params) => {
        const id = params.row.complainId;
        const checked = selectedComplaint.includes(id);
        return (
          <Checkbox checked={checked} inputProps={{ 'aria-label': `select complaint ${id}` }}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(id);
            }}
          />
        );
      }
    },
    { field: 'complainNumber', headerName: 'Complaint No', flex: 0.6, minWidth: 140 },
    { field: 'complainDate', headerName: 'Date', flex: 0.6, minWidth: 100, valueGetter: (value, row) => formatDate(row?.complainDate) },
    { field: 'LastFollowUpDate', headerName: 'Follow-Up', flex: 0.5, minWidth: 120, valueGetter: (value, row) => formatDate(row?.LastFollowUpDate) },
    {
      field: 'clientDetails', headerName: 'Client Details', flex: 2.5, minWidth: 260, sortable: false,
      // keep a simple valueGetter for copy/paste & accessibility
      valueGetter: (value, row) => {
        if (!row) return "";

        const { FirmName, ContactPerson, MobileNo, EmailId, GSTNo } = row;

        const lines = [];

        if (FirmName) lines.push(FirmName);

        const contactParts = [];
        if (ContactPerson) contactParts.push(ContactPerson);
        if (MobileNo) contactParts.push(MobileNo);
        if (EmailId) contactParts.push(EmailId);

        if (contactParts.length) lines.push(contactParts.join(" | "));
        if (GSTNo) lines.push(`GST: ${GSTNo}`);

        return lines.join(" \n ");
      },
      renderCell: (params) => {
        const row = params.row || {};
        const FirmName = row.FirmName || row.FirmName || '-';
        const ContactPerson = row.ContactPerson || row.clientContact || '';
        const MobileNo = row.MobileNo || row.Mobile || '';
        const EmailId = row.EmailId || row.emailId || row.Email || '';
        const Address = row.Address || row.address || '';
        const City = row.City || row.city || '';
        const GSTNo = row.GSTNo || row.GSTNo || '';

        return (
          <Box sx={{ py: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {/* Firm name as link-like bold text */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1877F2', textDecoration: 'underline', fontSize: '0.95rem', mb: 0.5, cursor: 'pointer' }} >
              {FirmName}
            </Typography>

            {/* Contact person row */}
            {ContactPerson ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                <PersonIcon fontSize="small" sx={{ color: '#FADA5E' }} />
                <Typography variant="body2">{ContactPerson}</Typography>
              </Stack>
            ) : null}

            {/* Mobile row */}
            {MobileNo ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                <PhoneAndroidIcon fontSize="small" sx={{ color: '#555' }} />
                <Typography variant="body2">{MobileNo}</Typography>
              </Stack>
            ) : null}

            {/* Email row */}
            {EmailId ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                <EmailIcon fontSize="small" sx={{ color: '#555' }} />
                <Typography variant="body2">{EmailId}</Typography>
              </Stack>
            ) : null}

            {/* Address / Location row */}
            {(Address || City) ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                <LocationOnIcon fontSize="small" sx={{ color: '#1877F2' }} />
                <Typography variant="body2">
                  {[Address, City].filter(Boolean).join(', ')}
                </Typography>
              </Stack>
            ) : null}

            {/* GST row (small) */}
            {GSTNo ? (<Typography variant="caption" display="block" sx={{ mt: 0.5 }}> GST: {GSTNo} </Typography>) : null}
          </Box>
        );
      }
    },
    {
      field: 'location', headerName: 'Location', flex: 0.5, minWidth: 160,
      valueGetter: (value, row) => {
        if (!row) return "";

        const c = toStringValue(row.City || row.city);
        const s = toStringValue(row.state || row.State || row.stateName);

        return c && s ? `${c}, ${s}` : (c || s || "");
      },
      // renderCell returns JSX to display stacked lines
      renderCell: (params) => {
        const c = toStringValue(params.row.City || params.row.city) || "—";
        const s = toStringValue(params.row.state || params.row.State || params.row.stateName) || "—";
        return (
          <Box>
            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1 }}>{c}</Typography>
            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1, color: 'text.secondary' }}>{s}</Typography>
          </Box>
        );
      }
    },
    { field: 'EmployeeName', headerName: 'Assigned', flex: 0.5, minWidth: 150, valueGetter: (value, row) => row?.EmployeeName || "Unassigned" },
  ];

  // Grid rows expect an 'id' property (we set it here)
  // const rows = inquiries.map((row) => ({ id: row.complainId, ...row }));

  // Mobile card renderer (one card per row, flex column)
  const MobileCards = ({ data }) => (
    <Box sx={{ mt: 3 }}>
      {
        selectedComplaint.length > 0 && (<Box sx={{ display: "flex", alignItems: "center", pl: 2 }}>
          <Checkbox checked={selectedComplaint.length === inquiries.length && inquiries.length > 0} onChange={(e) => handleSelectAll(e.target.checked)} />
          <Typography variant="subtitle1">{`${selectedComplaint.length} selected`}</Typography>
        </Box>)
      }

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: 2, mt: 1 }}>
        {data.map((complain) => (
          <Card key={complain.complainId} variant="outlined" sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox checked={selectedComplaint.includes(complain.complainId)} onChange={() => handleCheckboxChange(complain.complainId)} />
                    <Typography variant="h6" fontWeight="bold">{complain.complainNumber}</Typography>
                  </Box>
                  <Typography variant="caption">{formatDate(complain.complainDate)}</Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body2"><b>Client:</b></Typography>
                  <Typography variant="body2">{complain.FirmName}</Typography>
                  <Typography variant="body2">{complain.ContactPerson} • {complain.MobileNo}</Typography>
                  <Typography variant="body2">{complain.EmailId}</Typography>
                  {complain.GSTNo ? <Typography variant="body2">GST: {complain.GSTNo}</Typography> : null}
                </Box>

                <Box mt={1} display="flex" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2"><b>Location:</b></Typography>
                    <Typography variant="body2">{complain.City}, {complain.state}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="body2"><b>Follow-Up:</b></Typography>
                    <Typography variant="body2">{formatDate(complain.LastFollowUpDate)}</Typography>
                    <Typography variant="body2"><b>Assigned:</b> {complain.LastemployeeName || "Unassigned"}</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h6" sx={{ mb: 1 }}> Search Filters </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item size={{ xs: 6, sm: 3, md: 2 }}>
          <Dropdown label="Assign" name="assign" value={filters.assign} options={usersOptions} valueKey="UserId" labelKey="employeeName" onChange={handleAssignChange} size="small"
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": { height: 44, fontSize: "0.875rem" }
            }}
          />
        </Grid>

        <Grid item size={{ xs: 6, sm: 3, md: 2 }}>
          <Dropdown label="Re-Assign" name="reAssign" value={filters.reAssign} options={usersOptions} valueKey="UserId" labelKey="employeeName" onChange={handleReAssignChange} size="small"
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": { height: 44, fontSize: "0.875rem" }
            }}
          />
        </Grid>

        <Grid item size={{ xs: 6, sm: 2, md: 1 }}>
          <Button variant="outlined" color="success" onClick={fetchInquiries} fullWidth size="small"
            sx={{ height: 44, fontSize: "0.875rem", textTransform: "none" }}
          >
            Apply
          </Button>
        </Grid>

        <Grid item size={{ xs: 6, sm: 2, md: 1 }}>
          <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth size="small"
            sx={{ height: 44, fontSize: "0.875rem", textTransform: "none" }}
          >
            Submit
          </Button>
        </Grid>

        <Grid item size={{ xs: 6, sm: 2, md: 1 }}>
          <Button variant="outlined" color="secondary" onClick={handleResetFilters} fullWidth size="small"
            sx={{ height: 44, fontSize: "0.875rem", textTransform: "none" }}
          >
            Reset
          </Button>
        </Grid>

      </Grid>

      {/* {!loading && inquiries.length > 0 && (<Box sx={{ mt: 2, ml: 2 }}> <Typography variant="subtitle1"> Total Complaints: {inquiries.length} </Typography> </Box>)} */}
      {loading && (<Grid container justifyContent="center" sx={{ mt: 3 }}> <CircularProgress /> </Grid>)}
      {!loading && inquiries.length > 0 ? (
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
          <Box sx={{ p: { xs: 0.5, md: 0.5 } }}>
            {isDesktop ? (

              <Box sx={{ height: '74vh', width: '100%' }}>
                <DataGrid
                  rows={inquiries}
                  getRowId={(row) => row.complainId}
                  columns={columns}
                  getRowHeight={() => 'auto'}
                  pageSize={50}
                  rowsPerPageOptions={[50, 100, 150]}
                  disableSelectionOnClick
                  // don't use DataGrid's builtin checkboxSelection; using custom column above
                  sx={{
                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                    '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },
                    '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },
                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center'
                    },
                  }}
                  slots={{
                    noResultsOverlay: CustomNoDataOverlay
                  }}
                />
              </Box>

            ) : (
              <MobileCards data={inquiries} />
            )}
          </Box>
        </Paper>
      ) : (
        !loading && <Typography sx={{ mt: 3 }}>No complaints found.</Typography>
      )}
    </Box>
  );
};

export default ReAssignComplaintDetails;